<template>
    <ul id="container-tiles" class="tiles-wrap content-container">
        <Post
            v-for="post in posts"
            :key="post.link"
            :post="post"
        />
    </ul>
    <p v-if="!isLoading && posts.length===0 && !pageInit"
       style="text-align: center;"
    >Sorry, no post matched your criteria</p>

    <div ref="scrollFeature">&nbsp;</div>

    <div class="d-flex justify-content-center" v-if="isLoading">
        <div class="spinner-border" role="status"></div>
    </div>
</template>

<script setup>
    /* global Wookmark */
    import imagesLoaded from 'imagesloaded';
    import 'wookmark/wookmark.min.js';

    import Post from './post.vue';
    import { ref, onMounted, nextTick } from 'vue';
    import {getFiltersFromUrl} from './../dataParser.js';

    const posts = ref([]);
    const pageInit = ref(true);
    const scrollFeature = ref(null);
    const isLoading = ref(false);

    let noMoreContent = false;

    /*wait for images to be loaded and replace the ones
    that didnt load with default image */
    const asyncImagesLoaded = async () => {
        return new Promise((accept, reject) => {
            const imgLoad = imagesLoaded('#container-tiles');
            const handleImages = async () => {
                for (let i = 0, len = imgLoad.images.length; i < len; i++) {
                    let image = imgLoad.images[i];
                    // replace image if not loaded
                    if (!image.isLoaded) {
                        await replaceImageAsync(image.img, "/default-image-thumbnail.png");
                    }
                }
                accept();
            };
            const replaceImageAsync = async (imgElement, newSrc) => {
                return new Promise((resolve) => {
                    imgElement.onload = resolve;
                    imgElement.onerror = resolve;
                    imgElement.src = newSrc;
                });
            };
            imgLoad.on('always', () => {
                handleImages();
            });
        });
    };

    let wookmark = null;

    onMounted(async () => {
        wookmark = new Wookmark('#container-tiles', {
            fillEmptySpace: false, // Optional, fill the bottom of each column with widths of flexible height\
            offset: 20,
            itemWidth: 200,
            outerOffset: 20
        });

        const observer = new IntersectionObserver(
            async (entries) => {
                for (let entry of entries) {
                    if (entry.isIntersecting && !isLoading.value && !noMoreContent){
                        await loadContent();
                    }
                }
            },{rootMargin:"500px"}
        );
        observer.observe(scrollFeature.value);

        await loadContent();
        pageInit.value = false;
    });

    async function* search(term=null, filters={}, sort={}) {
        const pagefind = await import(/* webpackIgnore: true */ "/pagefind/pagefind.js");
        await import(/* webpackIgnore: true */ "/pagefind/pagefind-highlight.js");

        let searchResults = await pagefind.search(
            term,
            {
                sort,
                filters,
                verbose: false
            }
        );

        let postResults = searchResults.results;

        let limit = 30;

        for (let i=0; i<postResults.length; i+=limit) {
            let chunk = postResults.slice(i , i+limit);
            let awaitChunk = await Promise.all(chunk.map(r => r.data()));
            yield awaitChunk.map(post_data => {
                return {
                    title: post_data.meta.title,
                    // Should use post_data.excerpt, if the <mark> tags can be removed
                    excerpt: post_data.content.substring(0, 200),
                    link: post_data.url,
                    thumbnail: post_data.meta.image,
                    views: post_data.meta.views,
                    //get type value to use as data for the type-colorful-thumb element
                    type: post_data.filters.types && post_data.filters.types[0].split("-").join(" "),
                    //url for thumb element
                    url: post_data.filters.types && new URL(`/category/type/${post_data.filters.types[0]}`,window.location.origin)
                };
            });
        }
    }

    let {term, filters, sort} = getFiltersFromUrl();

    let query = search(term, filters, {[sort]: 'desc'});
    const loadContent = async () => {
        try {
            isLoading.value = true;

            let searchInput = document.getElementById('header-search');
            if (searchInput) {
                searchInput.value = term;
            }

            let next_results = (await query.next()).value;
            posts.value = [
                ...posts.value,
                ...next_results
            ];

            await nextTick();
            await asyncImagesLoaded();
            wookmark.initItems();
            wookmark.layout(true);
        } catch {
            noMoreContent = true;
        } finally {
            isLoading.value = false;
        }
    };

</script>

<style lang="less" scoped>

    @keyframes spinner-border {
      to { transform: rotate(360deg); }
    }

    .spinner-border {
        display: inline-block;
        width: 2rem;
        height: 2rem;
        vertical-align: text-bottom;
        border: .25em solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: spinner-border .75s linear infinite;
    }

    .d-flex {
        display: flex !important;
    }

    .justify-content-center {
        justify-content: center !important;
    }

    .tiles-wrap {
        display: none;
        list-style-type: none;
        margin: 10px 0;
        padding: 0;
        position: relative;

        li {
            color: #999;

            &:not([style]) {
                visibility: hidden;
            }

            &.wookmark-inactive {
                opacity: 0;
                visibility: hidden;
            }
        }

        img {
            display: block;
        }

        a {
            color: blue;
            text-align: center;
            width: 200px;
            height: 200px;
            font-size: 2em;
            font-weight: bold;
            text-decoration: none;
        }

        p {
            min-height: 65px;
            padding: 5px;
        }
    }

</style>
